import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  BarChart3,
  Bot,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Download,
  ExternalLink,
  FileCheck2,
  FileText,
  FolderSearch,
  GraduationCap,
  History,
  MessageSquareWarning,
  Printer,
  ShieldCheck,
  Tag,
  Users,
} from 'lucide-react';
import toast from 'react-hot-toast';

type CqcDomain = 'Safe' | 'Effective' | 'Caring' | 'Responsive' | 'Well-led';
type EvidenceStatus = 'current' | 'review_due' | 'gap';
type ComplaintStatus = 'open' | 'investigating' | 'resolved' | 'overdue';
type TrackerStatus = 'open' | 'monitoring' | 'closed' | 'overdue';
type ChecklistStatus = 'complete' | 'in_progress' | 'gap';

interface DomainScore {
  domain: CqcDomain;
  score: number;
  rating: 'Outstanding' | 'Good' | 'Requires improvement';
  evidenceCount: number;
  gaps: number;
}

interface EvidenceItem {
  id: string;
  title: string;
  domain: CqcDomain;
  qualityStatement: string;
  category: string;
  owner: string;
  status: EvidenceStatus;
  updatedAt: string;
  tags: string[];
}

interface Complaint {
  id: string;
  complainant: string;
  subject: string;
  receivedAt: string;
  dueAt: string;
  status: ComplaintStatus;
  linkedDomain: CqcDomain;
  action: string;
}

interface AuditTrailEvent {
  time: string;
  actor: string;
  event: string;
  relatedTo: string;
  outcome: string;
}

interface UkLegalRequirement {
  id: string;
  area: string;
  requirement: string;
  status: 'supported' | 'external_action' | 'needs_review';
  evidence: string;
  owner: string;
}

interface LegalDocumentLink {
  title: string;
  description: string;
  href: string;
}

interface CqcChecklistItem {
  id: string;
  domain: CqcDomain;
  requirement: string;
  status: ChecklistStatus;
  evidence: string;
  owner: string;
  dueAt: string;
}

interface IncidentTrackerItem {
  id: string;
  clientName: string;
  type: string;
  severity: 'low' | 'medium' | 'high';
  status: TrackerStatus;
  occurredAt: string;
  action: string;
}

interface SafeguardingTrackerItem {
  id: string;
  clientName: string;
  concern: string;
  status: TrackerStatus;
  referral: string;
  nextReview: string;
}

interface ReminderItem {
  id: string;
  title: string;
  owner: string;
  dueAt: string;
  status: 'current' | 'due_soon' | 'overdue';
}

interface EvidenceLibraryItem {
  id: string;
  title: string;
  cqcDomain: CqcDomain;
  evidenceType: string;
  sourceModule: string;
  owner: string;
  reviewFrequency: string;
  lastReviewed: string;
  nextReview: string;
  status: EvidenceStatus;
  storageLocation: string;
  qualityStatement: string;
  tags: string[];
}

interface PirReportSection {
  id: string;
  section: string;
  question: string;
  evidenceSummary: string;
  gaps: string;
  owner: string;
  readiness: number;
  status: ChecklistStatus;
}

interface WorkflowStage {
  id: string;
  title: string;
  owner: string;
  dueAt: string;
  status: 'complete' | 'active' | 'waiting' | 'overdue';
}

interface ComplianceWorkflow {
  id: string;
  type: 'Complaint' | 'Safeguarding';
  title: string;
  personAffected: string;
  openedAt: string;
  priority: 'low' | 'medium' | 'high';
  currentStage: string;
  stages: WorkflowStage[];
  learningAction: string;
}

interface PolicyDocument {
  id: string;
  title: string;
  category: string;
  owner: string;
  version: string;
  approvalStatus: 'approved' | 'review_due' | 'draft';
  lastApproved: string;
  nextReview: string;
  linkedEvidence: string;
}

interface TrainingMatrixRow {
  id: string;
  carerName: string;
  role: string;
  safeguarding: ReminderItem['status'];
  medication: ReminderItem['status'];
  movingHandling: ReminderItem['status'];
  infectionControl: ReminderItem['status'];
  dementia: ReminderItem['status'];
  nextExpiry: string;
  actionRequired: string;
}

interface DocumentExpiryItem {
  id: string;
  carerName: string;
  documentType: string;
  reference: string;
  expiresAt: string;
  status: ReminderItem['status'];
  action: string;
}

interface GdprRegisterItem {
  id: string;
  register: 'DPIA' | 'ROPA' | 'Lawful basis' | 'Data breach' | 'Processor' | 'Retention';
  title: string;
  dataCategory: string;
  lawfulBasis: string;
  owner: string;
  reviewDate: string;
  status: 'current' | 'needs_review' | 'in_progress';
  risk: 'low' | 'medium' | 'high';
}

const domainScores: DomainScore[] = [
  { domain: 'Safe', score: 82, rating: 'Good', evidenceCount: 18, gaps: 2 },
  { domain: 'Effective', score: 78, rating: 'Good', evidenceCount: 13, gaps: 3 },
  { domain: 'Caring', score: 86, rating: 'Good', evidenceCount: 11, gaps: 1 },
  { domain: 'Responsive', score: 74, rating: 'Requires improvement', evidenceCount: 10, gaps: 4 },
  { domain: 'Well-led', score: 80, rating: 'Good', evidenceCount: 16, gaps: 2 },
];

const evidenceItems: EvidenceItem[] = [
  {
    id: 'ev-001',
    title: 'Medication safety audit May 2026',
    domain: 'Safe',
    qualityStatement: 'Medicines optimisation',
    category: 'Processes',
    owner: 'Registered manager',
    status: 'current',
    updatedAt: '2026-05-20',
    tags: ['eMAR', 'PRN', 'MAR audit'],
  },
  {
    id: 'ev-002',
    title: 'Safeguarding concern escalation log',
    domain: 'Safe',
    qualityStatement: 'Safeguarding',
    category: 'Feedback from staff and leaders',
    owner: 'Safeguarding lead',
    status: 'current',
    updatedAt: '2026-05-18',
    tags: ['safeguarding', 'incident', 'training'],
  },
  {
    id: 'ev-003',
    title: 'Care plan review sample pack',
    domain: 'Effective',
    qualityStatement: 'Assessing needs',
    category: 'Processes',
    owner: 'Care coordinator',
    status: 'review_due',
    updatedAt: '2026-04-30',
    tags: ['care plans', 'reviews'],
  },
  {
    id: 'ev-004',
    title: 'Family feedback and communication records',
    domain: 'Caring',
    qualityStatement: 'Kindness, compassion and dignity',
    category: 'People experience',
    owner: 'Family liaison',
    status: 'current',
    updatedAt: '2026-05-19',
    tags: ['family portal', 'feedback'],
  },
  {
    id: 'ev-005',
    title: 'Complaints response improvement plan',
    domain: 'Responsive',
    qualityStatement: 'Listening to and involving people',
    category: 'Feedback from people',
    owner: 'Registered manager',
    status: 'gap',
    updatedAt: '2026-05-12',
    tags: ['complaints', 'Regulation 16'],
  },
  {
    id: 'ev-006',
    title: 'Governance meeting minutes',
    domain: 'Well-led',
    qualityStatement: 'Governance, management and sustainability',
    category: 'Processes',
    owner: 'Nominated individual',
    status: 'current',
    updatedAt: '2026-05-17',
    tags: ['governance', 'audit trail'],
  },
];

const initialComplaints: Complaint[] = [
  {
    id: 'cmp-001',
    complainant: 'Margaret Williams family',
    subject: 'Late evening visit communication',
    receivedAt: '2026-05-10',
    dueAt: '2026-05-24',
    status: 'investigating',
    linkedDomain: 'Responsive',
    action: 'Coordinator to confirm call procedure and update family communication note.',
  },
  {
    id: 'cmp-002',
    complainant: 'George Taylor',
    subject: 'Preference for same carer continuity',
    receivedAt: '2026-05-08',
    dueAt: '2026-05-22',
    status: 'resolved',
    linkedDomain: 'Caring',
    action: 'Preference logged in care plan and scheduling notes.',
  },
  {
    id: 'cmp-003',
    complainant: 'Anonymous staff concern',
    subject: 'Medication documentation consistency',
    receivedAt: '2026-05-12',
    dueAt: '2026-05-26',
    status: 'open',
    linkedDomain: 'Safe',
    action: 'Run medication competency refresher and review eMAR audit records.',
  },
];

const auditTrail: AuditTrailEvent[] = [
  {
    time: '2026-05-20 18:55',
    actor: 'Badan Catalin',
    event: 'PIR pack generated',
    relatedTo: 'CQC compliance',
    outcome: 'Local JSON evidence pack downloaded',
  },
  {
    time: '2026-05-20 18:44',
    actor: 'System',
    event: 'eMAR record saved',
    relatedTo: 'Medicines optimisation',
    outcome: 'MAR audit evidence tagged',
  },
  {
    time: '2026-05-20 18:21',
    actor: 'System',
    event: 'GP Connect readiness checked',
    relatedTo: 'Safe systems and pathways',
    outcome: 'Blocked pending NHS onboarding',
  },
  {
    time: '2026-05-19 16:10',
    actor: 'Registered manager',
    event: 'Complaint reviewed',
    relatedTo: 'Regulation 16',
    outcome: 'Action owner assigned',
  },
];

const pirSections = [
  'Service changes and registered manager details',
  'Safe care and treatment evidence',
  'Staffing, recruitment, training and supervision',
  'Medicines management and eMAR audit',
  'Complaints, safeguarding and learning culture',
  'People experience, family feedback and outcomes',
  'Governance, improvement plans and sustainability',
];

const cqcChecklist: CqcChecklistItem[] = [
  {
    id: 'cqc-safe-001',
    domain: 'Safe',
    requirement: 'Safeguarding, medicines, infection control, risk management and missed-visit escalation are evidenced.',
    status: 'in_progress',
    evidence: 'eMAR page, incident tracker, safeguarding tracker, live carer checks and audit trail.',
    owner: 'Registered manager',
    dueAt: '2026-06-05',
  },
  {
    id: 'cqc-effective-001',
    domain: 'Effective',
    requirement: 'Care plans, assessments, reviews, consent and staff competency are current.',
    status: 'in_progress',
    evidence: 'AI care plan review, missing field detection, training reminders and care plan history.',
    owner: 'Care coordinator',
    dueAt: '2026-06-10',
  },
  {
    id: 'cqc-caring-001',
    domain: 'Caring',
    requirement: 'Family feedback, dignity, preferences and communication records are available.',
    status: 'complete',
    evidence: 'Family portal updates, family-safe notes and standard communication records.',
    owner: 'Family liaison',
    dueAt: '2026-06-01',
  },
  {
    id: 'cqc-responsive-001',
    domain: 'Responsive',
    requirement: 'Complaints, concerns, missed visits and service improvements are tracked to closure.',
    status: 'gap',
    evidence: 'Complaints tracker exists; overdue review process needs final management approval.',
    owner: 'Registered manager',
    dueAt: '2026-05-28',
  },
  {
    id: 'cqc-wellled-001',
    domain: 'Well-led',
    requirement: 'Governance, audits, PIR preparation, policy reviews and quality improvement are evidenced.',
    status: 'in_progress',
    evidence: 'Q-Score, PIR draft, evidence tracker, audit trail and policy reminders.',
    owner: 'Nominated individual',
    dueAt: '2026-06-15',
  },
];

const incidentTracker: IncidentTrackerItem[] = [
  {
    id: 'inc-cqc-001',
    clientName: 'Margaret Williams',
    type: 'Fall / mobility near miss',
    severity: 'medium',
    status: 'monitoring',
    occurredAt: '2026-05-18',
    action: 'Risk assessment updated; family informed; next mobility review due.',
  },
  {
    id: 'inc-cqc-002',
    clientName: 'Eileen Cooper',
    type: 'Medication prompt delay',
    severity: 'high',
    status: 'open',
    occurredAt: '2026-05-20',
    action: 'Medication competency refresher and eMAR audit sample required.',
  },
  {
    id: 'inc-cqc-003',
    clientName: 'George Taylor',
    type: 'Late check-in',
    severity: 'low',
    status: 'closed',
    occurredAt: '2026-05-21',
    action: 'Schedule note updated and family message sent.',
  },
];

const safeguardingTracker: SafeguardingTrackerItem[] = [
  {
    id: 'sg-001',
    clientName: 'Rose Hughes',
    concern: 'Financial pressure concern raised by family',
    status: 'monitoring',
    referral: 'Internal safeguarding log; external referral not currently required',
    nextReview: '2026-05-29',
  },
  {
    id: 'sg-002',
    clientName: 'Arthur Bennett',
    concern: 'Home equipment risk after bed rail fault',
    status: 'open',
    referral: 'Equipment provider contacted; review evidence pending',
    nextReview: '2026-05-27',
  },
  {
    id: 'sg-003',
    clientName: 'Margaret Williams',
    concern: 'Medication documentation consistency',
    status: 'closed',
    referral: 'Resolved through MAR audit and staff supervision',
    nextReview: '2026-06-04',
  },
];

const policyReminders: ReminderItem[] = [
  { id: 'pol-001', title: 'Safeguarding adults policy', owner: 'Safeguarding lead', dueAt: '2026-06-07', status: 'due_soon' },
  { id: 'pol-002', title: 'Medication management policy', owner: 'Registered manager', dueAt: '2026-05-30', status: 'due_soon' },
  { id: 'pol-003', title: 'Complaints policy', owner: 'Nominated individual', dueAt: '2026-05-21', status: 'overdue' },
  { id: 'pol-004', title: 'Data protection and retention policy', owner: 'Data protection lead', dueAt: '2026-06-20', status: 'current' },
];

const trainingReminders: ReminderItem[] = [
  { id: 'tr-001', title: 'Safeguarding Adults Level 2 - Adrian Popescu', owner: 'Training lead', dueAt: '2026-06-12', status: 'due_soon' },
  { id: 'tr-002', title: 'Medication competency - Amelia Patel', owner: 'Registered manager', dueAt: '2026-05-24', status: 'due_soon' },
  { id: 'tr-003', title: 'Moving and handling - Daniel Moore', owner: 'Training lead', dueAt: '2026-05-19', status: 'overdue' },
  { id: 'tr-004', title: 'Infection prevention - Grace Morgan', owner: 'Training lead', dueAt: '2026-07-02', status: 'current' },
];

const evidenceLibrary: EvidenceLibraryItem[] = [
  {
    id: 'lib-001',
    title: 'eMAR medication safety audit pack',
    cqcDomain: 'Safe',
    evidenceType: 'Audit report',
    sourceModule: 'eMAR and Medication Safety',
    owner: 'Registered manager',
    reviewFrequency: 'Monthly',
    lastReviewed: '2026-05-20',
    nextReview: '2026-06-20',
    status: 'current',
    storageLocation: 'Medication Compliance Report / eMAR audit trail',
    qualityStatement: 'Medicines optimisation',
    tags: ['eMAR', 'PRN', 'missed medication', 'double-check'],
  },
  {
    id: 'lib-002',
    title: 'Live carer GPS and visit verification evidence',
    cqcDomain: 'Safe',
    evidenceType: 'Operational log',
    sourceModule: 'Live Carers',
    owner: 'Care coordinator',
    reviewFrequency: 'Weekly',
    lastReviewed: '2026-05-21',
    nextReview: '2026-05-28',
    status: 'current',
    storageLocation: 'Live carers dashboard and check-in/out audit',
    qualityStatement: 'Safe systems, pathways and transitions',
    tags: ['GPS', 'check-in', 'missed visits', 'audit'],
  },
  {
    id: 'lib-003',
    title: 'Care plan review and risk assessment evidence',
    cqcDomain: 'Effective',
    evidenceType: 'Care record sample',
    sourceModule: 'Care Plans',
    owner: 'Care coordinator',
    reviewFrequency: 'Monthly',
    lastReviewed: '2026-04-30',
    nextReview: '2026-05-30',
    status: 'review_due',
    storageLocation: 'Care plan AI review and care plan history',
    qualityStatement: 'Assessing needs',
    tags: ['care plan', 'risk assessment', 'missing fields'],
  },
  {
    id: 'lib-004',
    title: 'Family communication and consent evidence',
    cqcDomain: 'Caring',
    evidenceType: 'Family portal record',
    sourceModule: 'Family Portal',
    owner: 'Family liaison',
    reviewFrequency: 'Monthly',
    lastReviewed: '2026-05-19',
    nextReview: '2026-06-19',
    status: 'current',
    storageLocation: 'Family updates, acknowledgements and messages',
    qualityStatement: 'Kindness, compassion and dignity',
    tags: ['family', 'updates', 'consent', 'communication'],
  },
  {
    id: 'lib-005',
    title: 'Complaints Regulation 16 improvement evidence',
    cqcDomain: 'Responsive',
    evidenceType: 'Workflow record',
    sourceModule: 'Complaints Workflow',
    owner: 'Registered manager',
    reviewFrequency: 'Weekly',
    lastReviewed: '2026-05-12',
    nextReview: '2026-05-24',
    status: 'gap',
    storageLocation: 'Complaints workflow and learning actions',
    qualityStatement: 'Listening to and involving people',
    tags: ['complaints', 'learning', 'Regulation 16'],
  },
  {
    id: 'lib-006',
    title: 'Governance, policies and GDPR accountability pack',
    cqcDomain: 'Well-led',
    evidenceType: 'Governance register',
    sourceModule: 'Compliance and Enterprise Security',
    owner: 'Nominated individual',
    reviewFrequency: 'Monthly',
    lastReviewed: '2026-05-17',
    nextReview: '2026-06-17',
    status: 'current',
    storageLocation: 'Policy manager, audit trail, GDPR registers',
    qualityStatement: 'Governance, management and sustainability',
    tags: ['governance', 'GDPR', 'policy', 'audit'],
  },
];

const pirReportSections: PirReportSection[] = [
  {
    id: 'pir-safe',
    section: 'Safe',
    question: 'How does the service protect people from avoidable harm and abuse?',
    evidenceSummary: 'eMAR safety checks, GPS check-in evidence, safeguarding tracker, incident tracker and missed-visit alerts are available.',
    gaps: 'Attach manager sign-off to medication competency follow-up and unresolved safeguarding equipment evidence.',
    owner: 'Registered manager',
    readiness: 82,
    status: 'in_progress',
  },
  {
    id: 'pir-effective',
    section: 'Effective',
    question: 'How are needs assessed and outcomes monitored?',
    evidenceSummary: 'Care plan reviews, risk assessment prompts, task allocation and training reminders support assessment evidence.',
    gaps: 'Complete review_due care plan sample and link updated risk assessment evidence.',
    owner: 'Care coordinator',
    readiness: 78,
    status: 'in_progress',
  },
  {
    id: 'pir-caring',
    section: 'Caring',
    question: 'How does the service evidence dignity, respect and involvement?',
    evidenceSummary: 'Family portal updates, communication preferences, acknowledgements and family-visible summaries are tracked.',
    gaps: 'Add more real feedback examples after production go-live.',
    owner: 'Family liaison',
    readiness: 86,
    status: 'complete',
  },
  {
    id: 'pir-responsive',
    section: 'Responsive',
    question: 'How are complaints, concerns and changing needs handled?',
    evidenceSummary: 'Complaints workflow, concerns, missed visits and service improvement actions are visible.',
    gaps: 'Complete final management approval for complaints review and learning cycle.',
    owner: 'Registered manager',
    readiness: 74,
    status: 'gap',
  },
  {
    id: 'pir-well-led',
    section: 'Well-led',
    question: 'How does governance drive quality improvement?',
    evidenceSummary: 'Q-Score, audit trail, policy manager, training matrix, GDPR registers and PIR preparation pack are available.',
    gaps: 'Complete formal governance meeting sign-off and external compliance review.',
    owner: 'Nominated individual',
    readiness: 80,
    status: 'in_progress',
  },
];

const complianceWorkflows: ComplianceWorkflow[] = [
  {
    id: 'wf-complaint-001',
    type: 'Complaint',
    title: 'Late evening visit communication',
    personAffected: 'Margaret Williams family',
    openedAt: '2026-05-10',
    priority: 'medium',
    currentStage: 'Investigation and response',
    learningAction: 'Update family communication procedure and confirm escalation owner for late visits.',
    stages: [
      { id: 'stage-1', title: 'Acknowledge complaint', owner: 'Administrator', dueAt: '2026-05-12', status: 'complete' },
      { id: 'stage-2', title: 'Assign investigator', owner: 'Registered manager', dueAt: '2026-05-13', status: 'complete' },
      { id: 'stage-3', title: 'Collect evidence and call logs', owner: 'Care coordinator', dueAt: '2026-05-20', status: 'active' },
      { id: 'stage-4', title: 'Send written response', owner: 'Registered manager', dueAt: '2026-05-24', status: 'waiting' },
      { id: 'stage-5', title: 'Record learning action', owner: 'Nominated individual', dueAt: '2026-05-31', status: 'waiting' },
    ],
  },
  {
    id: 'wf-sg-001',
    type: 'Safeguarding',
    title: 'Home equipment risk after bed rail fault',
    personAffected: 'Arthur Bennett',
    openedAt: '2026-05-18',
    priority: 'high',
    currentStage: 'External evidence pending',
    learningAction: 'Add equipment-provider evidence and review whether risk assessment needs urgent update.',
    stages: [
      { id: 'stage-1', title: 'Record concern and immediate action', owner: 'Carer / admin', dueAt: '2026-05-18', status: 'complete' },
      { id: 'stage-2', title: 'Manager threshold decision', owner: 'Safeguarding lead', dueAt: '2026-05-19', status: 'complete' },
      { id: 'stage-3', title: 'External referral / provider contact', owner: 'Safeguarding lead', dueAt: '2026-05-20', status: 'active' },
      { id: 'stage-4', title: 'Update risk assessment and care plan', owner: 'Care coordinator', dueAt: '2026-05-27', status: 'waiting' },
      { id: 'stage-5', title: 'Close with evidence and learning', owner: 'Registered manager', dueAt: '2026-06-03', status: 'waiting' },
    ],
  },
];

const policyDocuments: PolicyDocument[] = [
  { id: 'policy-001', title: 'Safeguarding Adults Policy', category: 'Safety', owner: 'Safeguarding lead', version: 'v1.4', approvalStatus: 'approved', lastApproved: '2026-03-10', nextReview: '2026-06-07', linkedEvidence: 'Safeguarding tracker and training matrix' },
  { id: 'policy-002', title: 'Medication Management Policy', category: 'Medication', owner: 'Registered manager', version: 'v1.2', approvalStatus: 'review_due', lastApproved: '2026-02-28', nextReview: '2026-05-30', linkedEvidence: 'eMAR, PRN protocol and medication audit' },
  { id: 'policy-003', title: 'Complaints Policy', category: 'Responsive', owner: 'Nominated individual', version: 'v1.1', approvalStatus: 'review_due', lastApproved: '2025-11-21', nextReview: '2026-05-21', linkedEvidence: 'Complaints workflow and learning actions' },
  { id: 'policy-004', title: 'Data Protection and Retention Policy', category: 'GDPR', owner: 'Data protection lead', version: 'v1.0', approvalStatus: 'approved', lastApproved: '2026-04-20', nextReview: '2026-06-20', linkedEvidence: 'DPIA, ROPA and access logs' },
  { id: 'policy-005', title: 'Recruitment and DBS Policy', category: 'Workforce', owner: 'HR lead', version: 'draft', approvalStatus: 'draft', lastApproved: '-', nextReview: '2026-06-10', linkedEvidence: 'DBS/document expiry tracker and carer files' },
];

const trainingMatrix: TrainingMatrixRow[] = [
  { id: 'tm-001', carerName: 'Adrian Popescu', role: 'Care Assistant', safeguarding: 'due_soon', medication: 'current', movingHandling: 'current', infectionControl: 'current', dementia: 'current', nextExpiry: '2026-06-12', actionRequired: 'Book safeguarding refresher.' },
  { id: 'tm-002', carerName: 'Daniel Moore', role: 'Senior Carer', safeguarding: 'current', medication: 'current', movingHandling: 'overdue', infectionControl: 'current', dementia: 'due_soon', nextExpiry: '2026-05-19', actionRequired: 'Restrict moving/handling allocations until refreshed.' },
  { id: 'tm-003', carerName: 'Amelia Patel', role: 'Care Assistant', safeguarding: 'current', medication: 'due_soon', movingHandling: 'current', infectionControl: 'current', dementia: 'current', nextExpiry: '2026-05-24', actionRequired: 'Complete medication competency check.' },
  { id: 'tm-004', carerName: 'Oliver Reed', role: 'Night Care Assistant', safeguarding: 'current', medication: 'current', movingHandling: 'current', infectionControl: 'current', dementia: 'current', nextExpiry: '2026-08-18', actionRequired: 'No immediate action.' },
  { id: 'tm-005', carerName: 'Grace Morgan', role: 'Live-in Carer', safeguarding: 'current', medication: 'current', movingHandling: 'due_soon', infectionControl: 'current', dementia: 'current', nextExpiry: '2026-07-02', actionRequired: 'Prepare moving/handling refresher.' },
];

const documentExpiryTracker: DocumentExpiryItem[] = [
  { id: 'doc-001', carerName: 'Adrian Popescu', documentType: 'Enhanced DBS', reference: 'DBS-AD-2026', expiresAt: '2026-08-15', status: 'current', action: 'Review again 60 days before expiry.' },
  { id: 'doc-002', carerName: 'Daniel Moore', documentType: 'Moving and handling certificate', reference: 'MH-DM-2025', expiresAt: '2026-05-19', status: 'overdue', action: 'Book refresher and restrict relevant visits until complete.' },
  { id: 'doc-003', carerName: 'Amelia Patel', documentType: 'Medication competency', reference: 'MED-AP-2025', expiresAt: '2026-05-24', status: 'due_soon', action: 'Manager assessment required.' },
  { id: 'doc-004', carerName: 'Oliver Reed', documentType: 'Right to work evidence', reference: 'RTW-OR-2026', expiresAt: '2027-01-10', status: 'current', action: 'No immediate action.' },
  { id: 'doc-005', carerName: 'Grace Morgan', documentType: 'Public liability / car insurance', reference: 'CAR-GM-2026', expiresAt: '2026-06-18', status: 'due_soon', action: 'Request renewed certificate before expiry.' },
];

const gdprRegisters: GdprRegisterItem[] = [
  { id: 'gdpr-001', register: 'DPIA', title: 'Live carer GPS tracking DPIA', dataCategory: 'Location, visit logs, staff identifiers', lawfulBasis: 'Legitimate interests / legal obligation review required', owner: 'Data protection lead', reviewDate: '2026-06-05', status: 'in_progress', risk: 'high' },
  { id: 'gdpr-002', register: 'ROPA', title: 'Care delivery records processing activity', dataCategory: 'Health and social care records', lawfulBasis: 'Provision of health or social care', owner: 'Registered manager', reviewDate: '2026-06-12', status: 'needs_review', risk: 'high' },
  { id: 'gdpr-003', register: 'Lawful basis', title: 'Family portal communication', dataCategory: 'Family contact details and visit summaries', lawfulBasis: 'Legitimate interests / consent where required', owner: 'Family liaison', reviewDate: '2026-06-10', status: 'current', risk: 'medium' },
  { id: 'gdpr-004', register: 'Processor', title: 'OpenAI API assistant processing review', dataCategory: 'Operational prompts and limited care context', lawfulBasis: 'Processor contract and minimisation review required', owner: 'Technical administrator', reviewDate: '2026-06-01', status: 'in_progress', risk: 'medium' },
  { id: 'gdpr-005', register: 'Retention', title: 'Payroll, visit and eMAR retention schedule', dataCategory: 'Employment, care and medication records', lawfulBasis: 'Legal obligation / contract', owner: 'Information governance', reviewDate: '2026-07-01', status: 'needs_review', risk: 'medium' },
];

const ukLegalRequirements: UkLegalRequirement[] = [
  {
    id: 'uk-001',
    area: 'Software ownership',
    requirement: 'Project marked as private proprietary software owned by Dorobat Marius-Dorinel.',
    status: 'supported',
    evidence: 'LICENSE, OWNERSHIP.md and package metadata updated to UNLICENSED.',
    owner: 'Dorobat Marius-Dorinel',
  },
  {
    id: 'uk-002',
    area: 'Companies House',
    requirement: 'Company and PSC/beneficial ownership records must match the real operating business.',
    status: 'external_action',
    evidence: 'Software ownership register exists in repo; Companies House records must be maintained externally.',
    owner: 'Director / company secretary',
  },
  {
    id: 'uk-003',
    area: 'ICO / UK GDPR',
    requirement: 'Maintain lawful basis, privacy notices, retention schedule, DPIA and breach process.',
    status: 'needs_review',
    evidence: 'Access controls, role separation and server-side secret storage implemented; formal governance requires approval.',
    owner: 'Data protection lead',
  },
  {
    id: 'uk-004',
    area: 'CQC',
    requirement: 'Confirm regulated activity registration for personal care in England.',
    status: 'external_action',
    evidence: 'CQC evidence dashboard and PIR preparation pack implemented; official registration is external.',
    owner: 'Registered manager / nominated individual',
  },
  {
    id: 'uk-005',
    area: 'NHS DSP Toolkit',
    requirement: 'Complete DSPT before connecting to NHS systems or GP Connect.',
    status: 'external_action',
    evidence: 'GP Connect readiness blocks production NHS use until onboarding is complete.',
    owner: 'Information governance lead',
  },
  {
    id: 'uk-006',
    area: 'Security and secrets',
    requirement: 'Keep production secrets server-side and protect administrative access.',
    status: 'supported',
    evidence: 'API Keys panel saves through secured server endpoint; secrets are not returned to browser.',
    owner: 'Technical administrator',
  },
  {
    id: 'uk-007',
    area: 'Payroll',
    requirement: 'Validate carer pay calculations and payslip outputs with payroll/accounting process.',
    status: 'needs_review',
    evidence: 'Payroll page calculates from verified carer hours and generates PDF payslips.',
    owner: 'Payroll lead',
  },
  {
    id: 'uk-008',
    area: 'Clinical safety',
    requirement: 'Medication/eMAR workflows must be clinically reviewed before live medication decisions.',
    status: 'needs_review',
    evidence: 'eMAR readiness workflow exists; clinical safety case and hazard log require approval.',
    owner: 'Clinical safety lead',
  },
];

const legalDocumentLinks: LegalDocumentLink[] = [
  {
    title: 'Ownership, Licence and Copyright Declaration',
    description: 'Internal proprietary ownership, licence and copyright record for Dorobat Marius-Dorinel.',
    href: '/legal/01-ownership-rights-dorobat-marius-dorinel.pdf',
  },
];

const statusStyles: Record<EvidenceStatus, string> = {
  current: 'bg-emerald-100 text-emerald-800',
  review_due: 'bg-amber-100 text-amber-800',
  gap: 'bg-red-100 text-red-800',
};

const complaintStyles: Record<ComplaintStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  investigating: 'bg-amber-100 text-amber-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
};

const trackerStyles: Record<TrackerStatus, string> = {
  open: 'bg-blue-100 text-blue-800',
  monitoring: 'bg-amber-100 text-amber-800',
  closed: 'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
};

const checklistStyles: Record<ChecklistStatus, string> = {
  complete: 'bg-emerald-100 text-emerald-800',
  in_progress: 'bg-amber-100 text-amber-800',
  gap: 'bg-red-100 text-red-800',
};

const reminderStyles: Record<ReminderItem['status'], string> = {
  current: 'bg-emerald-100 text-emerald-800',
  due_soon: 'bg-amber-100 text-amber-800',
  overdue: 'bg-red-100 text-red-800',
};

const workflowStageStyles: Record<WorkflowStage['status'], string> = {
  complete: 'bg-emerald-100 text-emerald-800',
  active: 'bg-blue-100 text-blue-800',
  waiting: 'bg-slate-100 text-slate-700',
  overdue: 'bg-red-100 text-red-800',
};

const policyStatusStyles: Record<PolicyDocument['approvalStatus'], string> = {
  approved: 'bg-emerald-100 text-emerald-800',
  review_due: 'bg-amber-100 text-amber-800',
  draft: 'bg-slate-100 text-slate-700',
};

const gdprStatusStyles: Record<GdprRegisterItem['status'], string> = {
  current: 'bg-emerald-100 text-emerald-800',
  needs_review: 'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-800',
};

const ratingStyles: Record<DomainScore['rating'], string> = {
  Outstanding: 'bg-purple-100 text-purple-800',
  Good: 'bg-emerald-100 text-emerald-800',
  'Requires improvement': 'bg-amber-100 text-amber-800',
};

function sanitizePdfText(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\x20-\x7E\n\r\t]/g, ' ')
    .replace(/[()\\]/g, (match) => `\\${match}`)
    .trim();
}

function wrapPdfText(text: string, maxLength: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let current = '';

  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function buildCompliancePdf(title: string, lines: string[]): Blob {
  const rawLines = [
    title,
    'FUCHSIA HOME CARE CATALIN',
    'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
    `Generated: ${new Date().toLocaleString('en-GB')}`,
    '',
    ...lines.flatMap((line) => wrapPdfText(line, 92)),
  ].map(sanitizePdfText);

  const linesPerPage = 56;
  const pages: string[][] = [];
  for (let index = 0; index < rawLines.length; index += linesPerPage) {
    pages.push(rawLines.slice(index, index + linesPerPage));
  }

  const objects: string[] = [];
  const addObject = (body: string) => {
    objects.push(body);
    return objects.length;
  };

  const catalogId = addObject('<< /Type /Catalog /Pages 2 0 R >>');
  const pagesId = addObject('');
  const fontId = addObject('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>');
  const pageIds: number[] = [];

  pages.forEach((pageLines) => {
    const content = [
      'BT',
      '/F1 10 Tf',
      '48 790 Td',
      '14 TL',
      ...pageLines.map((line, lineIndex) =>
        lineIndex === 0 ? `/F1 16 Tf (${line}) Tj /F1 10 Tf T*` : `(${line}) Tj T*`
      ),
      'ET',
    ].join('\n');
    const contentId = addObject(`<< /Length ${content.length} >>\nstream\n${content}\nendstream`);
    const pageId = addObject(
      `<< /Type /Page /Parent ${pagesId} 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`
    );
    pageIds.push(pageId);
  });

  objects[pagesId - 1] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;
  const chunks = ['%PDF-1.4\n'];
  const offsets = [0];
  objects.forEach((object, index) => {
    offsets.push(chunks.join('').length);
    chunks.push(`${index + 1} 0 obj\n${object}\nendobj\n`);
  });
  const xrefOffset = chunks.join('').length;
  chunks.push(`xref\n0 ${objects.length + 1}\n`);
  chunks.push('0000000000 65535 f \n');
  offsets.slice(1).forEach((offset) => {
    chunks.push(`${String(offset).padStart(10, '0')} 00000 n \n`);
  });
  chunks.push(`trailer\n<< /Size ${objects.length + 1} /Root ${catalogId} 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`);

  return new Blob(chunks, { type: 'application/pdf' });
}

function downloadPdf(title: string, lines: string[]) {
  const url = URL.createObjectURL(buildCompliancePdf(title, lines));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function printPdf(title: string, lines: string[]): boolean {
  const url = URL.createObjectURL(buildCompliancePdf(title, lines));
  const pdfWindow = window.open(url, '_blank', 'noopener,noreferrer');
  if (!pdfWindow) {
    URL.revokeObjectURL(url);
    return false;
  }
  window.setTimeout(() => {
    pdfWindow.print();
    URL.revokeObjectURL(url);
  }, 700);
  return true;
}

export const ComplianceDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'checklist'
    | 'library'
    | 'evidence'
    | 'pir'
    | 'workflows'
    | 'trackers'
    | 'policy'
    | 'training'
    | 'reminders'
    | 'complaints'
    | 'gdpr'
    | 'audit'
    | 'legal'
  >('overview');
  const [domainFilter, setDomainFilter] = useState<'All' | CqcDomain>('All');
  const [complaints, setComplaints] = useState(initialComplaints);
  const [aiGapAnalysis, setAiGapAnalysis] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const qScore = Math.round(
    domainScores.reduce((total, item) => total + item.score, 0) / domainScores.length
  );
  const openIncidents = incidentTracker.filter((item) => item.status !== 'closed').length;
  const openSafeguarding = safeguardingTracker.filter((item) => item.status !== 'closed').length;
  const overduePolicies = policyReminders.filter((item) => item.status === 'overdue').length;
  const overdueTraining = trainingReminders.filter((item) => item.status === 'overdue').length;
  const checklistGaps = cqcChecklist.filter((item) => item.status === 'gap').length;
  const openComplaints = complaints.filter((complaint) => complaint.status !== 'resolved').length;
  const evidenceGaps = evidenceItems.filter((item) => item.status === 'gap').length;
  const reviewDue = evidenceItems.filter((item) => item.status === 'review_due').length;
  const externalLegalActions = ukLegalRequirements.filter((item) => item.status !== 'supported').length;
  const libraryGaps = evidenceLibrary.filter((item) => item.status === 'gap').length;
  const workflowActive = complianceWorkflows.filter((workflow) => workflow.stages.some((stage) => stage.status === 'active' || stage.status === 'overdue')).length;
  const policiesNeedingReview = policyDocuments.filter((item) => item.approvalStatus !== 'approved').length;
  const trainingActions = trainingMatrix.filter((item) =>
    [item.safeguarding, item.medication, item.movingHandling, item.infectionControl, item.dementia].some((status) => status !== 'current')
  ).length;
  const documentActions = documentExpiryTracker.filter((item) => item.status !== 'current').length;
  const gdprActions = gdprRegisters.filter((item) => item.status !== 'current').length;
  const operationalRiskScore = Math.min(
    100,
    Math.round(
      evidenceGaps * 9 +
        libraryGaps * 9 +
        reviewDue * 5 +
        openComplaints * 6 +
        openIncidents * 7 +
        openSafeguarding * 10 +
        overduePolicies * 8 +
        overdueTraining * 8 +
        checklistGaps * 10 +
        workflowActive * 6 +
        policiesNeedingReview * 4 +
        trainingActions * 5 +
        documentActions * 5 +
        gdprActions * 6
    )
  );

  const filteredEvidence = domainFilter === 'All'
    ? evidenceItems
    : evidenceItems.filter((item) => item.domain === domainFilter);

  const pirPack = useMemo(() => ({
    generatedAt: new Date().toISOString(),
    provider: 'FUCHSIA HOME CARE CATALIN',
    location: 'Yarmouth Business Park, Suffolk Rd, Great Yarmouth, NR31 0LN',
    qScore,
    domains: domainScores,
    evidence: evidenceItems,
    evidenceLibrary,
    complaints,
    workflows: complianceWorkflows,
    auditTrail,
    sections: pirSections,
    detailedPirSections: pirReportSections,
    checklist: cqcChecklist,
    incidents: incidentTracker,
    safeguarding: safeguardingTracker,
    policyDocuments,
    trainingMatrix,
    documentExpiryTracker,
    gdprRegisters,
    policyReminders,
    trainingReminders,
    operationalRiskScore,
    note: 'Local preparation pack. Submit only through the official CQC PIR link when requested.',
  }), [complaints, qScore, operationalRiskScore]);

  const downloadPirPack = () => {
    const blob = new Blob([JSON.stringify(pirPack, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cqc-pir-preparation-pack-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('PIR preparation pack generated');
  };

  const compliancePdfLines = [
    `Local Q-Score: ${qScore}%`,
    `Operational risk score: ${operationalRiskScore}/100`,
    `Evidence gaps: ${evidenceGaps}; review due: ${reviewDue}; open complaints: ${openComplaints}; open incidents: ${openIncidents}; open safeguarding items: ${openSafeguarding}.`,
    '',
    'CQC evidence library:',
    ...evidenceLibrary.map((item) => `${item.cqcDomain} - ${item.title} - ${item.status.replace('_', ' ')} - source ${item.sourceModule} - next review ${item.nextReview}.`),
    '',
    'CQC checklist:',
    ...cqcChecklist.map((item) => `${item.domain} - ${item.status.replace('_', ' ')} - ${item.requirement} Owner: ${item.owner}. Due: ${item.dueAt}.`),
    '',
    'Evidence tracker:',
    ...evidenceItems.map((item) => `${item.domain} - ${item.title} - ${item.status.replace('_', ' ')} - ${item.owner}.`),
    '',
    'Complaints:',
    ...complaints.map((item) => `${item.subject} - ${item.status} - due ${item.dueAt}. Action: ${item.action}`),
    '',
    'Incidents:',
    ...incidentTracker.map((item) => `${item.clientName} - ${item.type} - ${item.severity} - ${item.status}. ${item.action}`),
    '',
    'Safeguarding:',
    ...safeguardingTracker.map((item) => `${item.clientName} - ${item.concern} - ${item.status}. ${item.referral}`),
    '',
    'Workflow status:',
    ...complianceWorkflows.map((item) => `${item.type} - ${item.title} - current stage ${item.currentStage}. Learning action: ${item.learningAction}`),
    '',
    'Policy manager:',
    ...policyDocuments.map((item) => `${item.title} - ${item.approvalStatus.replace('_', ' ')} - owner ${item.owner} - next review ${item.nextReview}.`),
    '',
    'Training matrix:',
    ...trainingMatrix.map((item) => `${item.carerName} - next expiry ${item.nextExpiry}. Action: ${item.actionRequired}`),
    '',
    'DBS/document expiry tracker:',
    ...documentExpiryTracker.map((item) => `${item.carerName} - ${item.documentType} - ${item.status.replace('_', ' ')} - expires ${item.expiresAt}. ${item.action}`),
    '',
    'DPIA, ROPA and GDPR registers:',
    ...gdprRegisters.map((item) => `${item.register} - ${item.title} - ${item.status.replace('_', ' ')} - risk ${item.risk}. Review: ${item.reviewDate}.`),
    '',
    'Policy reminders:',
    ...policyReminders.map((item) => `${item.title} - ${item.status.replace('_', ' ')} - owner ${item.owner} - due ${item.dueAt}.`),
    '',
    'Training reminders:',
    ...trainingReminders.map((item) => `${item.title} - ${item.status.replace('_', ' ')} - owner ${item.owner} - due ${item.dueAt}.`),
    '',
    'Important note: This is a local preparation document. It does not replace official CQC registration, statutory notifications, PIR submission through the official CQC link, or professional legal/compliance review.',
  ];

  const pirPdfLines = [
    'Draft PIR preparation summary for adult social care.',
    'Do not submit this document to CQC as a replacement for the official PIR form.',
    '',
    ...pirSections.map((section) => `PIR section covered: ${section}`),
    '',
    `Safe: ${domainScores.find((item) => item.domain === 'Safe')?.score}% local readiness.`,
    `Effective: ${domainScores.find((item) => item.domain === 'Effective')?.score}% local readiness.`,
    `Caring: ${domainScores.find((item) => item.domain === 'Caring')?.score}% local readiness.`,
    `Responsive: ${domainScores.find((item) => item.domain === 'Responsive')?.score}% local readiness.`,
    `Well-led: ${domainScores.find((item) => item.domain === 'Well-led')?.score}% local readiness.`,
    '',
    'Detailed PIR sections:',
    ...pirReportSections.map((item) => `${item.section}: ${item.question} Readiness ${item.readiness}%. Gaps: ${item.gaps}`),
    '',
    'Priority gaps:',
    ...evidenceItems.filter((item) => item.status !== 'current').map((item) => `${item.domain}: ${item.title} - ${item.status.replace('_', ' ')}.`),
  ];

  const handleDownloadCompliancePdf = () => {
    downloadPdf('CQC Compliance Evidence Pack', compliancePdfLines);
    toast.success('Compliance PDF downloaded');
  };

  const handlePrintCompliancePdf = () => {
    if (!printPdf('CQC Compliance Evidence Pack', compliancePdfLines)) {
      toast.error('Allow pop-ups to print the PDF');
    }
  };

  const handleDownloadPirPdf = () => {
    downloadPdf('CQC PIR Draft Preparation', pirPdfLines);
    toast.success('PIR draft PDF downloaded');
  };

  const runAiGapAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const response = await fetch('/api/ai/admin-assistant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'ADMIN',
          page: 'cqc-compliance',
          question: 'Run a CQC compliance gap analysis. Confirm understanding first, then recommend the highest priority gaps and next actions. Do not claim legal certification.',
          context: {
            qScore,
            operationalRiskScore,
            domainScores,
            cqcChecklist,
            evidenceItems,
            complaints,
            incidentTracker,
            safeguardingTracker,
            policyReminders,
            trainingReminders,
          },
        }),
      });
      if (!response.ok) throw new Error(`AI returned ${response.status}`);
      const payload = (await response.json()) as { text?: string };
      setAiGapAnalysis(payload.text?.trim() || '');
    } catch {
      setAiGapAnalysis([
        'I understand that you want a CQC compliance gap analysis for the current dashboard.',
        '',
        'Recommended next steps:',
        '1. Close the Responsive domain complaints gap first because it affects learning and service improvement evidence.',
        '2. Review overdue complaints policy and moving/handling training before any inspection preparation meeting.',
        '3. Attach evidence to the medication delay incident and safeguarding equipment review.',
        '4. Download the compliance PDF and PIR draft for registered manager review.',
        '',
        `Result: Current local Q-Score is ${qScore}% and operational risk score is ${operationalRiskScore}/100. This is a preparation tool, not legal certification.`,
      ].join('\n'));
      toast.error('AI unavailable. Local compliance analysis was generated.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const markComplaintResolved = (id: string) => {
    setComplaints((items) =>
      items.map((item) =>
        item.id === id ? { ...item, status: 'resolved' } : item
      )
    );
    toast.success('Complaint marked as resolved');
  };

  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex items-start gap-4">
            <div className="rounded-md bg-[#6c3a80]/10 p-3 text-[#6c3a80]">
              <ShieldCheck className="h-7 w-7" />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
                CQC Compliance
              </p>
              <h1 className="mt-1 text-3xl font-bold text-slate-950">CQC Advanced Hub</h1>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
                Prepare evidence against CQC's five key questions, monitor a local Q-Score,
                track complaints under Regulation 16, build a PIR preparation pack and keep an
                audit trail for inspection readiness.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleDownloadCompliancePdf}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white transition hover:bg-[#5e3474]"
            >
              <Download className="h-4 w-4" />
              Download PDF
            </button>
            <button
              type="button"
              onClick={handlePrintCompliancePdf}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <Printer className="h-4 w-4" />
              Print PDF
            </button>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-6">
        <MetricCard title="Q-Score" value={`${qScore}%`} subtitle="Local readiness score" icon={<BarChart3 className="h-5 w-5" />} />
        <MetricCard title="Risk Score" value={`${operationalRiskScore}/100`} subtitle="Operational risk" icon={<AlertTriangle className="h-5 w-5" />} tone={operationalRiskScore > 50 ? 'warning' : 'success'} />
        <MetricCard title="Evidence Gaps" value={String(evidenceGaps + libraryGaps)} subtitle="Needs action" icon={<FolderSearch className="h-5 w-5" />} tone={evidenceGaps + libraryGaps > 0 ? 'warning' : 'success'} />
        <MetricCard title="Incidents" value={String(openIncidents)} subtitle="Open / monitoring" icon={<MessageSquareWarning className="h-5 w-5" />} tone={openIncidents > 0 ? 'warning' : 'success'} />
        <MetricCard title="Open Complaints" value={String(openComplaints)} subtitle="Regulation 16 tracking" icon={<MessageSquareWarning className="h-5 w-5" />} tone={openComplaints > 0 ? 'warning' : 'success'} />
        <MetricCard title="Reminders" value={String(overduePolicies + overdueTraining)} subtitle="Overdue items" icon={<CalendarClock className="h-5 w-5" />} tone={overduePolicies + overdueTraining > 0 ? 'warning' : 'success'} />
      </section>

      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4">
        <div className="flex gap-3 text-sm leading-6 text-amber-950">
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>
            This dashboard supports inspection preparation. It does not replace CQC registration,
            statutory notifications, PIR submission through CQC's official link, or management review
            of real policies, records and practice.
          </p>
        </div>
      </section>

      <div className="border-b border-slate-200">
        <nav className="-mb-px flex flex-wrap gap-6">
          {[
            { id: 'overview', label: 'Q-Score' },
            { id: 'checklist', label: 'CQC Checklist' },
            { id: 'library', label: 'Evidence Library' },
            { id: 'evidence', label: 'Evidence Tags' },
            { id: 'pir', label: 'PIR Reports' },
            { id: 'workflows', label: 'Workflows' },
            { id: 'trackers', label: 'Incidents & Safeguarding' },
            { id: 'policy', label: 'Policy Manager' },
            { id: 'training', label: 'Training Matrix' },
            { id: 'reminders', label: 'Policy & Training' },
            { id: 'complaints', label: 'Complaints' },
            { id: 'gdpr', label: 'DPIA / ROPA / GDPR' },
            { id: 'audit', label: 'Audit Trail' },
            { id: 'legal', label: 'UK Legal' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`border-b-2 px-1 py-4 text-sm font-semibold transition ${
                activeTab === tab.id
                  ? 'border-[#6c3a80] text-[#6c3a80]'
                  : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'overview' && (
        <section className="space-y-6">
          <div className="grid gap-4 xl:grid-cols-5">
            {domainScores.map((item) => (
              <article key={item.domain} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-bold text-slate-950">{item.domain}</h2>
                    <p className="mt-1 text-sm text-slate-500">{item.evidenceCount} evidence items</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${ratingStyles[item.rating]}`}>
                    {item.rating}
                  </span>
                </div>
                <div className="mt-5">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-700">Score</span>
                    <span className="font-semibold text-slate-950">{item.score}%</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-100">
                    <div className="h-2 rounded-full bg-[#6c3a80]" style={{ width: `${item.score}%` }} />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="text-slate-600">Gaps</span>
                  <span className={item.gaps > 0 ? 'font-semibold text-amber-700' : 'font-semibold text-emerald-700'}>
                    {item.gaps}
                  </span>
                </div>
              </article>
            ))}
          </div>

          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="flex items-center gap-3">
                  <Bot className="h-5 w-5 text-[#6c3a80]" />
                  <h2 className="text-xl font-bold text-slate-950">AI Compliance Gap Analysis</h2>
                </div>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Reviews checklist gaps, evidence status, complaints, incidents, safeguarding, policy reminders and training expiry reminders.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void runAiGapAnalysis()}
                disabled={isAnalyzing}
                className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474] disabled:opacity-60"
              >
                <Bot className="h-4 w-4" />
                {isAnalyzing ? 'Analyzing...' : 'Run AI Analysis'}
              </button>
            </div>
            <p className="mt-4 whitespace-pre-line rounded-lg bg-slate-50 p-4 text-sm leading-6 text-slate-700">
              {aiGapAnalysis || `I understand that you want a CQC compliance gap view. Recommended next steps:\n1. Review Responsive gaps, overdue policy reviews and overdue training first.\n2. Attach evidence to open incidents and safeguarding records.\n3. Export the compliance PDF before the management review.\n\nResult: Local Q-Score is ${qScore}% and operational risk score is ${operationalRiskScore}/100.`}
            </p>
          </article>
        </section>
      )}

      {activeTab === 'checklist' && (
        <section className="grid gap-4 xl:grid-cols-2">
          {cqcChecklist.map((item) => (
            <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.domain}</p>
                  <h2 className="mt-1 text-lg font-bold text-slate-950">{item.requirement}</h2>
                </div>
                <span className={`rounded-full px-2 py-1 text-xs font-semibold ${checklistStyles[item.status]}`}>
                  {item.status.replace('_', ' ')}
                </span>
              </div>
              <p className="mt-4 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">{item.evidence}</p>
              <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                <Detail label="Owner" value={item.owner} />
                <Detail label="Due" value={item.dueAt} />
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'library' && (
        <section className="space-y-4">
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <FolderSearch className="h-5 w-5 text-[#6c3a80]" />
              <div>
                <h2 className="text-xl font-bold text-slate-950">CQC Evidence Library</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Central index of evidence mapped to CQC domains, quality statements, owners, source modules and review dates.
                </p>
              </div>
            </div>
          </article>

          <div className="grid gap-4 xl:grid-cols-2">
            {evidenceLibrary.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.cqcDomain} - {item.evidenceType}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{item.title}</h2>
                    <p className="mt-1 text-sm text-slate-600">{item.qualityStatement}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[item.status]}`}>
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <Detail label="Source" value={item.sourceModule} />
                  <Detail label="Owner" value={item.owner} />
                  <Detail label="Review" value={item.reviewFrequency} />
                  <Detail label="Next review" value={item.nextReview} />
                  <Detail label="Storage" value={item.storageLocation} />
                  <Detail label="Last reviewed" value={item.lastReviewed} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'evidence' && (
        <section className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {(['All', 'Safe', 'Effective', 'Caring', 'Responsive', 'Well-led'] as Array<'All' | CqcDomain>).map((domain) => (
              <button
                key={domain}
                type="button"
                onClick={() => setDomainFilter(domain)}
                className={`rounded-md border px-3 py-2 text-sm font-semibold ${
                  domainFilter === domain
                    ? 'border-[#6c3a80] bg-[#6c3a80] text-white'
                    : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
              >
                {domain}
              </button>
            ))}
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            {filteredEvidence.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.domain}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{item.title}</h2>
                    <p className="mt-1 text-sm text-slate-600">{item.qualityStatement}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${statusStyles[item.status]}`}>
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
                  <Detail label="Category" value={item.category} />
                  <Detail label="Owner" value={item.owner} />
                  <Detail label="Updated" value={item.updatedAt} />
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      <Tag className="h-3 w-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'pir' && (
        <section className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">PIR Preparation Pack</h2>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              CQC sends a unique PIR link to the registered manager. This export is a local
              preparation pack only, designed to organise answers and evidence before the official
              PIR is completed.
            </p>
            <button
              type="button"
              onClick={downloadPirPack}
              className="mt-5 inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Download className="h-4 w-4" />
              Generate PIR JSON
            </button>
            <button
              type="button"
              onClick={handleDownloadPirPdf}
              className="ml-0 mt-3 inline-flex h-10 items-center gap-2 rounded-md bg-[#6c3a80] px-4 text-sm font-semibold text-white hover:bg-[#5e3474] sm:ml-3"
            >
              <Download className="h-4 w-4" />
              Download PIR Draft PDF
            </button>
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-bold text-slate-950">PIR Sections Covered</h2>
            <div className="mt-4 grid gap-3">
              {pirSections.map((section) => (
                <div key={section} className="flex gap-3 rounded-md bg-slate-50 p-3 text-sm text-slate-700">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600" />
                  {section}
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm xl:col-span-2">
            <div className="flex items-center gap-3">
              <ClipboardCheck className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Complete PIR Report Builder</h2>
            </div>
            <div className="mt-5 grid gap-4 xl:grid-cols-2">
              {pirReportSections.map((item) => (
                <div key={item.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.section}</p>
                      <h3 className="mt-1 font-bold text-slate-950">{item.question}</h3>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${checklistStyles[item.status]}`}>
                      {item.readiness}%
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-700">{item.evidenceSummary}</p>
                  <p className="mt-3 rounded-md bg-white p-3 text-sm leading-6 text-slate-700">
                    <span className="font-semibold text-slate-950">Gap / action:</span> {item.gaps}
                  </p>
                  <p className="mt-3 text-sm font-medium text-slate-600">Owner: {item.owner}</p>
                </div>
              ))}
            </div>
          </article>
        </section>
      )}

      {activeTab === 'workflows' && (
        <section className="space-y-6">
          {complianceWorkflows.map((workflow) => (
            <article key={workflow.id} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase text-[#6c3a80]">{workflow.type} workflow</p>
                  <h2 className="mt-1 text-xl font-bold text-slate-950">{workflow.title}</h2>
                  <p className="mt-2 text-sm text-slate-600">
                    Person affected: {workflow.personAffected} | Opened: {workflow.openedAt} | Priority: {workflow.priority}
                  </p>
                </div>
                <span className={`rounded-full px-3 py-1 text-sm font-semibold ${
                  workflow.priority === 'high' ? 'bg-red-100 text-red-800' : workflow.priority === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {workflow.currentStage}
                </span>
              </div>

              <div className="mt-5 grid gap-3 xl:grid-cols-5">
                {workflow.stages.map((stage, index) => (
                  <div key={stage.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                    <div className="flex items-center justify-between gap-2">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#6c3a80] text-xs font-bold text-white">
                        {index + 1}
                      </span>
                      <span className={`rounded-full px-2 py-1 text-xs font-semibold ${workflowStageStyles[stage.status]}`}>
                        {stage.status}
                      </span>
                    </div>
                    <h3 className="mt-3 font-bold text-slate-950">{stage.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">Owner: {stage.owner}</p>
                    <p className="mt-1 text-sm text-slate-600">Due: {stage.dueAt}</p>
                  </div>
                ))}
              </div>

              <p className="mt-5 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                <span className="font-semibold text-slate-950">Learning action:</span> {workflow.learningAction}
              </p>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'trackers' && (
        <section className="grid gap-6 xl:grid-cols-2">
          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <MessageSquareWarning className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Incident Tracker</h2>
            </div>
            <div className="mt-5 space-y-3">
              {incidentTracker.map((item) => (
                <div key={item.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-950">{item.clientName}</p>
                      <p className="mt-1 text-sm text-slate-600">{item.type}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${trackerStyles[item.status]}`}>{item.status}</span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-700">{item.action}</p>
                  <p className="mt-2 text-xs font-semibold uppercase text-slate-500">Severity: {item.severity} | Occurred: {item.occurredAt}</p>
                </div>
              ))}
            </div>
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Safeguarding Tracker</h2>
            </div>
            <div className="mt-5 space-y-3">
              {safeguardingTracker.map((item) => (
                <div key={item.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-950">{item.clientName}</p>
                      <p className="mt-1 text-sm text-slate-600">{item.concern}</p>
                    </div>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${trackerStyles[item.status]}`}>{item.status}</span>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-700">{item.referral}</p>
                  <p className="mt-2 text-xs font-semibold uppercase text-slate-500">Next review: {item.nextReview}</p>
                </div>
              ))}
            </div>
          </article>
        </section>
      )}

      {activeTab === 'policy' && (
        <section className="space-y-4">
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-[#6c3a80]" />
              <div>
                <h2 className="text-xl font-bold text-slate-950">Policy Manager</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Tracks policy owner, version, review date, approval state and linked CQC evidence.
                </p>
              </div>
            </div>
          </article>

          <div className="grid gap-4 xl:grid-cols-2">
            {policyDocuments.map((policy) => (
              <article key={policy.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{policy.category} - {policy.version}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{policy.title}</h2>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${policyStatusStyles[policy.approvalStatus]}`}>
                    {policy.approvalStatus.replace('_', ' ')}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <Detail label="Owner" value={policy.owner} />
                  <Detail label="Last approved" value={policy.lastApproved} />
                  <Detail label="Next review" value={policy.nextReview} />
                  <Detail label="Linked evidence" value={policy.linkedEvidence} />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'training' && (
        <section className="space-y-6">
          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-[#6c3a80]" />
              <div>
                <h2 className="text-xl font-bold text-slate-950">Training Matrix and DBS / Document Expiry Tracker</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Workforce compliance view for core training, DBS, right-to-work and required employment documents.
                </p>
              </div>
            </div>
          </article>

          <article className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5">
              <h2 className="text-xl font-bold text-slate-950">Training Matrix</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-3">Carer</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Safeguarding</th>
                    <th className="px-4 py-3">Medication</th>
                    <th className="px-4 py-3">Moving</th>
                    <th className="px-4 py-3">Infection</th>
                    <th className="px-4 py-3">Dementia</th>
                    <th className="px-4 py-3">Next expiry</th>
                    <th className="px-4 py-3">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {trainingMatrix.map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-3 font-medium text-slate-950">{row.carerName}</td>
                      <td className="px-4 py-3 text-slate-600">{row.role}</td>
                      {[row.safeguarding, row.medication, row.movingHandling, row.infectionControl, row.dementia].map((status, index) => (
                        <td key={`${row.id}-${index}`} className="px-4 py-3">
                          <span className={`rounded-full px-2 py-1 text-xs font-semibold ${reminderStyles[status]}`}>
                            {status.replace('_', ' ')}
                          </span>
                        </td>
                      ))}
                      <td className="px-4 py-3 text-slate-600">{row.nextExpiry}</td>
                      <td className="px-4 py-3 text-slate-600">{row.actionRequired}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

          <div className="grid gap-4 xl:grid-cols-2">
            {documentExpiryTracker.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.carerName}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{item.documentType}</h2>
                    <p className="mt-1 text-sm text-slate-600">{item.reference}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${reminderStyles[item.status]}`}>
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="mt-4 text-sm font-medium text-slate-600">Expires: {item.expiresAt}</p>
                <p className="mt-3 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">{item.action}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'reminders' && (
        <section className="grid gap-6 xl:grid-cols-2">
          <ReminderPanel
            title="Policy Review Reminders"
            icon={<CalendarClock className="h-5 w-5 text-[#6c3a80]" />}
            items={policyReminders}
          />
          <ReminderPanel
            title="Training Expiry Reminders"
            icon={<GraduationCap className="h-5 w-5 text-[#6c3a80]" />}
            items={trainingReminders}
          />
        </section>
      )}

      {activeTab === 'complaints' && (
        <section className="space-y-4">
          {complaints.map((complaint) => (
            <article key={complaint.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-bold text-slate-950">{complaint.subject}</h2>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${complaintStyles[complaint.status]}`}>
                      {complaint.status}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600">Complainant: {complaint.complainant}</p>
                  <p className="mt-1 text-sm text-slate-600">Linked domain: {complaint.linkedDomain}</p>
                  <p className="mt-3 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">{complaint.action}</p>
                </div>
                <div className="min-w-52 space-y-2 text-sm text-slate-600">
                  <p>Received: {complaint.receivedAt}</p>
                  <p>Due: {complaint.dueAt}</p>
                  {complaint.status !== 'resolved' && (
                    <button
                      type="button"
                      onClick={() => markComplaintResolved(complaint.id)}
                      className="mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Mark resolved
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </section>
      )}

      {activeTab === 'gdpr' && (
        <section className="space-y-4">
          <article className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
            DPIA, ROPA and GDPR registers are internal accountability records. They must be completed with real processing details, reviewed by the responsible person and aligned with the organisation's privacy notices and processor contracts.
          </article>

          <div className="grid gap-4 xl:grid-cols-2">
            {gdprRegisters.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.register}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{item.title}</h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">{item.dataCategory}</p>
                  </div>
                  <span className={`rounded-full px-2 py-1 text-xs font-semibold ${gdprStatusStyles[item.status]}`}>
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
                  <Detail label="Lawful basis" value={item.lawfulBasis} />
                  <Detail label="Owner" value={item.owner} />
                  <Detail label="Review date" value={item.reviewDate} />
                  <Detail label="Risk" value={item.risk} />
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {activeTab === 'audit' && (
        <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-5">
            <div className="flex items-center gap-3">
              <History className="h-5 w-5 text-[#6c3a80]" />
              <h2 className="text-xl font-bold text-slate-950">Inspection Audit Trail</h2>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Actor</th>
                  <th className="px-4 py-3">Event</th>
                  <th className="px-4 py-3">Related to</th>
                  <th className="px-4 py-3">Outcome</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {auditTrail.map((event) => (
                  <tr key={`${event.time}-${event.event}`}>
                    <td className="px-4 py-3 text-slate-600">{event.time}</td>
                    <td className="px-4 py-3 font-medium text-slate-950">{event.actor}</td>
                    <td className="px-4 py-3 text-slate-600">{event.event}</td>
                    <td className="px-4 py-3 text-slate-600">{event.relatedTo}</td>
                    <td className="px-4 py-3 text-slate-600">{event.outcome}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === 'legal' && (
        <section className="space-y-4">
          <article className="rounded-lg border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-950">
            This checklist supports governance, but it does not replace official Companies House,
            ICO, CQC, NHS DSP Toolkit, payroll, clinical safety or solicitor review.
          </article>

          <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-[#6c3a80]" />
                  <h2 className="text-xl font-bold text-slate-950">Ownership Document</h2>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  PDF record for proprietary ownership, licence and copyright evidence.
                </p>
              </div>
              <span className="rounded-md bg-[#6c3a80] px-3 py-2 text-sm font-semibold text-white">
                {legalDocumentLinks.length} PDFs
              </span>
            </div>

            <div className="mt-5 grid gap-3 lg:grid-cols-3">
              {legalDocumentLinks.map((document) => (
                <a
                  key={document.href}
                  href={document.href}
                  target="_blank"
                  rel="noreferrer"
                  className="group flex min-h-32 flex-col justify-between rounded-lg border border-slate-200 bg-slate-50 p-4 transition hover:border-[#6c3a80] hover:bg-white hover:shadow-sm"
                >
                  <span>
                    <span className="flex items-start justify-between gap-3">
                      <span className="font-semibold leading-5 text-slate-950">{document.title}</span>
                      <ExternalLink className="h-4 w-4 flex-shrink-0 text-slate-400 group-hover:text-[#6c3a80]" />
                    </span>
                    <span className="mt-2 block text-sm leading-5 text-slate-600">{document.description}</span>
                  </span>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6c3a80]">
                    <Download className="h-4 w-4" />
                    Open PDF
                  </span>
                </a>
              ))}
            </div>
          </article>

          <div className="grid gap-4 xl:grid-cols-2">
            {ukLegalRequirements.map((item) => (
              <article key={item.id} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase text-[#6c3a80]">{item.area}</p>
                    <h2 className="mt-1 text-lg font-bold text-slate-950">{item.requirement}</h2>
                  </div>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      item.status === 'supported'
                        ? 'bg-emerald-100 text-emerald-800'
                        : item.status === 'external_action'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {item.status.replace('_', ' ')}
                  </span>
                </div>
                <p className="mt-4 rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                  {item.evidence}
                </p>
                <p className="mt-3 text-sm font-medium text-slate-600">Owner: {item.owner}</p>
              </article>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  tone = 'default',
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}) {
  const colors = {
    default: 'border-slate-200 bg-white',
    success: 'border-emerald-200 bg-emerald-50',
    warning: 'border-amber-200 bg-amber-50',
  };

  return (
    <article className={`rounded-lg border p-4 shadow-sm ${colors[tone]}`}>
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-slate-500">{title}</p>
        <div className="text-[#6c3a80]">{icon}</div>
      </div>
      <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
      <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
    </article>
  );
}

function ReminderPanel({
  title,
  icon,
  items,
}: {
  title: string;
  icon: React.ReactNode;
  items: ReminderItem[];
}) {
  return (
    <article className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-center gap-3">
        {icon}
        <h2 className="text-xl font-bold text-slate-950">{title}</h2>
      </div>
      <div className="mt-5 space-y-3">
        {items.map((item) => (
          <div key={item.id} className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-semibold text-slate-950">{item.title}</p>
                <p className="mt-1 text-sm text-slate-600">Owner: {item.owner}</p>
              </div>
              <span className={`rounded-full px-2 py-1 text-xs font-semibold ${reminderStyles[item.status]}`}>
                {item.status.replace('_', ' ')}
              </span>
            </div>
            <p className="mt-3 text-sm font-medium text-slate-600">Due: {item.dueAt}</p>
          </div>
        ))}
      </div>
    </article>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase text-slate-500">{label}</p>
      <p className="mt-1 font-medium text-slate-950">{value}</p>
    </div>
  );
}

export default ComplianceDashboard;
